const SIGNUP_URL_FALLBACK =
  'https://develop.mb3.na.materialbank.com/na/en-US/registration'

let signupUrlFallbackWarned = false

// Exposed for test isolation only — resets the module-scoped warn-once flag.
export function __resetSignupUrlFallbackWarning(): void {
  signupUrlFallbackWarned = false
}

function isValidAbsoluteUrl(value: string): boolean {
  try {
    new URL(value)
    return true
  } catch {
    return false
  }
}

// Returns the configured MB3 sign-up URL, or null when it is unset, invalid,
// and/or we are running in production. Non-production falls back to the
// develop URL with a warn-once. Returning null fails closed: the CTA is
// disabled, which prevents silently redirecting real users to the develop
// environment OR throwing from a corrupted env var.
export function getSignupUrl(): string | null {
  const fromEnv = process.env.NEXT_PUBLIC_MB3_SIGNUP_URL
  if (fromEnv) {
    if (isValidAbsoluteUrl(fromEnv)) {
      return fromEnv
    }

    if (!signupUrlFallbackWarned) {
      console.error(
        `[mtag-authentication] NEXT_PUBLIC_MB3_SIGNUP_URL is set to an invalid URL (${fromEnv}) — Create an account CTA disabled.`,
      )
      signupUrlFallbackWarned = true
    }

    return null
  }

  if (process.env.NODE_ENV === 'production') {
    if (!signupUrlFallbackWarned) {
      console.error(
        '[mtag-authentication] NEXT_PUBLIC_MB3_SIGNUP_URL is not set in production — Create an account CTA disabled.',
      )
      signupUrlFallbackWarned = true
    }

    return null
  }

  if (!signupUrlFallbackWarned) {
    console.warn(
      '[mtag-authentication] NEXT_PUBLIC_MB3_SIGNUP_URL is not set — falling back to the develop URL. Set this env var explicitly in stage/prod.',
    )
    signupUrlFallbackWarned = true
  }

  return SIGNUP_URL_FALLBACK
}

// Path appended to the MB2 storefront base to reach its sign-in page. Kept in
// sync with mb3-frontend, which builds the same URL off NEXT_PUBLIC_MB_BASE_URL.
const MB2_LOGIN_PATH = '/customer/account/login/?callbackUrl=%2F'

let mb2LoginUrlWarned = false

// Exposed for test isolation only — resets the module-scoped warn-once flag.
export function __resetMb2LoginUrlWarning(): void {
  mb2LoginUrlWarned = false
}

// Returns the MB2 (Magento) sign-in URL used to bounce non-migrated users back
// to MB2, or null when the MB base URL is unset/invalid. Fails closed (null): a
// missing base disables the redirect rather than sending users somewhere broken.
// Reuses the same NEXT_PUBLIC_MB_BASE_URL storefront base as mb3-frontend.
export function getMb2LoginUrl(): string | null {
  const baseUrl = process.env.NEXT_PUBLIC_MB_BASE_URL

  if (baseUrl && isValidAbsoluteUrl(baseUrl)) {
    return `${baseUrl.replace(/\/$/, '')}${MB2_LOGIN_PATH}`
  }

  if (!mb2LoginUrlWarned) {
    console.error(
      '[mtag-authentication] NEXT_PUBLIC_MB_BASE_URL is not set to a valid URL — MB2 migration redirect disabled.',
    )
    mb2LoginUrlWarned = true
  }

  return null
}
